// Работа без интернета (02.10.2026): держит справочник товаров на устройстве свежим и отправляет на сервер
// документы, сохранённые без связи, как только интернет возвращается. Стоит в шапке программы.
import { useEffect, useSyncExternalStore } from 'react';
import { Badge, Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useWarehouse } from '../hooks.ts';
import { notifyError, notifySuccess } from '../notify.ts';
import { refreshCatalog } from './catalog.ts';
import { outbox, outboxInit, syncAll } from './outbox.ts';
import { rcInit, rcQueue, rcSync } from './rcqueue.ts';

export const useOutbox = () => useSyncExternalStore(outbox.subscribe, outbox.pending);
export const useRcQueue = () => useSyncExternalStore(rcQueue.subscribe, rcQueue.all);

export function OfflineSync() {
  const qc = useQueryClient();
  const warehouseId = useWarehouse();
  const pending = useOutbox();
  const scans = useRcQueue();

  useEffect(() => {
    void refreshCatalog(warehouseId);
    const t = setInterval(() => void refreshCatalog(warehouseId), 15 * 60_000);
    return () => clearInterval(t);
  }, [warehouseId]);

  useEffect(() => {
    const run = () => void syncAll((d, r) => {
      const name = d.kind === 'posting' ? 'Оприходование' : 'Списание';
      if (r.ok) notifySuccess(`${name} ${r.doc.number} ${r.doc.status === 'posted' ? 'проведено' : 'сохранено в черновиках'} — связь вернулась`);
      else if (!r.offline) notifyError(`${name}${r.savedAsDraft ? ` ${r.savedAsDraft.number}` : ''} не проведено`,
        `${r.error}${r.savedAsDraft ? ' Документ сохранён черновиком — исправьте и проведите.' : ''}`);
      void qc.invalidateQueries({ queryKey: ['stockDocs'] });
      void qc.invalidateQueries({ queryKey: ['stocks'] });
    }).then(() => rcSync((r) => {
      if (r.ok && r.item.kind === 'post') notifySuccess('Приёмка подтверждена — связь вернулась, товар доступен для продажи');
      if (!r.ok) notifyError(r.item.kind === 'post' ? 'Приёмка не подтверждена' : `Скан не принят: ${r.item.view?.name ?? ''}`,
        r.item.kind === 'post' ? `${r.error}. Откройте приёмку и подтвердите её.` : `${r.error}. Строка осталась в приёмке с пометкой — проверьте.`);
      for (const k of ['receipt', 'receipts', 'receiptsExpected', 'stocks']) void qc.invalidateQueries({ queryKey: [k] });
    })).then(() => refreshCatalog(warehouseId));
    void Promise.all([outboxInit(), rcInit()]).then(run);
    window.addEventListener('online', run);
    const t = setInterval(() => { if (outbox.pending().some((d) => !d.error) || rcQueue.all().some((i) => !i.error)) run(); }, 20_000);
    return () => { window.removeEventListener('online', run); clearInterval(t); };
  }, [qc, warehouseId]);

  const total = pending.length + scans.length;
  if (!total) return null;
  const errors = pending.filter((d) => d.error).length + scans.filter((i) => i.error).length;
  return (
    <Tooltip multiline w={300} label={errors
      ? 'Часть сохранённого сервер не принял — откройте документ или приёмку, там видно причину.'
      : 'Документы и сканы приёмки сохранены на устройстве. Они уйдут на сервер сами, как только появится интернет. Страницу можно закрывать.'}>
      <Badge color={errors ? 'red' : 'orange'} variant="light" size="lg" data-testid="outbox-badge">
        ждут отправки: {total}
      </Badge>
    </Tooltip>
  );
}
